import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Combobox,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import { IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import {
  useAttachParty,
  useCreateParty,
  useDetachParty,
  useEntryParties,
  useSearchParties,
} from '../../queries/parties';
import type { PartyRole } from '../../types/api';

interface Props {
  entryId: string;
  role: PartyRole;
  title: string;
}

export function PartyPickerForEntry({ entryId, role, title }: Props) {
  const [query, setQuery] = useState('');
  const { data: results = [] } = useSearchParties(query);
  const { data: attached = [] } = useEntryParties(entryId);
  const attachedForRole = useMemo(() => attached.filter((p) => p.role === role), [attached, role]);
  const createM = useCreateParty();
  const attachM = useAttachParty();
  const detachM = useDetachParty();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const alreadyAttachedIds = new Set(attachedForRole.map((p) => p.party_id));
  const candidates = results.filter((r) => !alreadyAttachedIds.has(r.id));
  const exactMatch = candidates.find((c) => c.canonical_name.toLowerCase() === query.toLowerCase());

  async function attachExisting(partyId: string) {
    await attachM.mutateAsync({ entry_id: entryId, party_id: partyId, role });
    setQuery('');
    combobox.closeDropdown();
  }

  async function createAndAttach() {
    if (!query.trim()) return;
    const { party } = await createM.mutateAsync({ canonical_name: query.trim() });
    await attachM.mutateAsync({ entry_id: entryId, party_id: party.id, role });
    setQuery('');
    combobox.closeDropdown();
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Text size="xs" c="dimmed">
          {attachedForRole.length} party{attachedForRole.length === 1 ? '' : 'ies'}
        </Text>
      </Group>

      <Group gap="xs" wrap="wrap">
        {attachedForRole.length === 0 && (
          <Text size="xs" c="dimmed">
            None assigned
          </Text>
        )}
        {attachedForRole.map((p) => (
          <Badge
            key={p.id}
            variant="light"
            rightSection={
              <Tooltip label="Remove">
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="gray"
                  onClick={() => detachM.mutate(p.id)}
                  aria-label="remove party"
                >
                  <IconX size={12} />
                </ActionIcon>
              </Tooltip>
            }
          >
            {p.party_canonical_name ?? p.party_id.slice(0, 8)}
          </Badge>
        ))}
      </Group>

      <Combobox store={combobox} onOptionSubmit={(val) => attachExisting(val)}>
        <Combobox.Target>
          <TextInput
            leftSection={<IconSearch size={14} />}
            placeholder={`Search or create ${role}`}
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
            }}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            size="xs"
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options mah={220} style={{ overflowY: 'auto' }}>
            {candidates.length === 0 && (
              <Combobox.Empty>
                {query.trim() ? 'No match - press Create' : 'Type to search'}
              </Combobox.Empty>
            )}
            {candidates.map((p) => (
              <Combobox.Option value={p.id} key={p.id}>
                <Group justify="space-between">
                  <Text size="sm">{p.canonical_name}</Text>
                  <Badge size="xs" variant="light">
                    {p.party_type}
                  </Badge>
                </Group>
              </Combobox.Option>
            ))}
          </Combobox.Options>
          {query.trim() && !exactMatch && (
            <Box>
              <Divider />
              <Button
                leftSection={<IconPlus size={14} />}
                variant="subtle"
                fullWidth
                size="xs"
                onMouseDown={(e) => e.preventDefault()}
                onClick={createAndAttach}
                loading={createM.isPending || attachM.isPending}
              >
                Create "{query.trim()}" and attach as {role}
              </Button>
            </Box>
          )}
        </Combobox.Dropdown>
      </Combobox>
    </Stack>
  );
}
